import React, { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from 'react-query'
import { Clock, Square, CheckCircle2, X, Play, Loader2, GripVertical } from 'lucide-react'
import { toast } from 'sonner'
import { BarChart, Bar, XAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts'
import { format, parseISO, isPast, isToday } from 'date-fns'
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import {
  Tooltip as ShadTooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { globalTaskService } from '@/services/globalTaskService'
import { timeEntryService } from '@/services/timeEntryService'
import { useTimerStore } from '@/store/timerStore'
import PriorityBadge from '@/components/ProjectCentral/PriorityBadge'
import TaskDisplayId from '@/components/ProjectCentral/TaskDisplayId'
import TimerStopDialog from '@/components/ProjectCentral/TimerStopDialog'

const formatDurationFromSeconds = (seconds) => {
  if (!seconds) return '0m'
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  if (h && m) return `${h}h ${m}m`
  if (h) return `${h}h`
  return `${m}m`
}

const formatElapsed = (startTime) => {
  const secs = Math.floor((Date.now() - new Date(startTime)) / 1000)
  const h = Math.floor(secs / 3600)
  const m = Math.floor((secs % 3600) / 60)
  const s = secs % 60
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

const priorityOrder = { critical: 4, high: 3, medium: 2, low: 1 }

const SortableTaskRow = ({ task, isTracking, overdueInfo, onTaskClick, onStartTimer, onStopTimer, startingTaskId, elapsed }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: task.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      className={cn(
        'flex items-center gap-3 px-3 py-2.5 text-sm border-b last:border-b-0 bg-background',
        isTracking && 'bg-primary/5',
        isDragging && 'opacity-50 z-50 shadow-md'
      )}
    >
      <button
        {...listeners}
        className="shrink-0 cursor-grab active:cursor-grabbing text-muted-foreground/40 hover:text-muted-foreground/70 transition-colors"
        tabIndex={-1}
      >
        <GripVertical className="h-3.5 w-3.5" />
      </button>

      <TooltipProvider delayDuration={0}>
        <ShadTooltip>
          <TooltipTrigger asChild>
            <span
              className="w-2 h-2 rounded-full shrink-0"
              style={{ backgroundColor: task.project?.color || '#3B82F6' }}
            />
          </TooltipTrigger>
          <TooltipContent side="top" className="text-xs">
            {task.project?.name}
          </TooltipContent>
        </ShadTooltip>
      </TooltipProvider>

      <button onClick={() => onTaskClick(task)} className="flex items-center gap-3 flex-1 min-w-0 text-left">
        <TaskDisplayId projectId={task.project?.id} projectKey={task.project?.key} taskId={task.id} taskNumber={task.task_number} taskType={task.type} />
        <span className="flex-1 truncate font-medium">{task.title}</span>
      </button>

      {overdueInfo === 'overdue' && (
        <span className="text-[10px] font-medium text-red-600 bg-red-50 px-1.5 py-0.5 rounded shrink-0">Overdue</span>
      )}
      {overdueInfo === 'due_today' && (
        <span className="text-[10px] font-medium text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded shrink-0">Due today</span>
      )}

      {/* Timer button */}
      <div className="shrink-0">
        {isTracking ? (
          <div className="flex items-center gap-1.5">
            <Clock className="h-3 w-3 text-primary animate-pulse" />
            <span className="text-xs font-mono font-medium text-primary tabular-nums">{elapsed}</span>
            <Button
              size="sm"
              variant="outline"
              className="h-6 w-6 p-0 border-destructive text-destructive hover:bg-destructive hover:text-white"
              onClick={(e) => { e.stopPropagation(); onStopTimer() }}
            >
              <Square className="h-2.5 w-2.5 fill-current" />
            </Button>
          </div>
        ) : (
          <Button
            size="sm"
            variant="ghost"
            className="h-6 w-6 p-0 text-muted-foreground hover:text-primary"
            onClick={(e) => { e.stopPropagation(); onStartTimer(task) }}
            disabled={startingTaskId === task.id}
          >
            {startingTaskId === task.id
              ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
              : <Play className="h-3.5 w-3.5 fill-current" />
            }
          </Button>
        )}
      </div>

      <span className="text-xs text-muted-foreground shrink-0 hidden sm:inline w-20 text-right">{task.status?.name}</span>
      <PriorityBadge priority={task.priority} />
    </div>
  )
}

const TASK_ORDER_KEY = 'foryou-task-order'

const ForYou = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { activeTimer, startTimer, stopTimer } = useTimerStore()
  const [elapsed, setElapsed] = useState('')
  const [stopOpen, setStopOpen] = useState(false)
  const [stopLoading, setStopLoading] = useState(false)
  const [sortBy, setSortBy] = useState('custom')
  const [filterType, setFilterType] = useState('all')
  const [dateFilter, setDateFilter] = useState(null)
  const [startingTaskId, setStartingTaskId] = useState(null)
  const [conflictTask, setConflictTask] = useState(null)
  const [customOrder, setCustomOrder] = useState(() => {
    try { return JSON.parse(localStorage.getItem(TASK_ORDER_KEY)) || null } catch { return null }
  })

  useEffect(() => {
    if (!activeTimer?.startTime) return
    const interval = setInterval(() => setElapsed(formatElapsed(activeTimer.startTime)), 1000)
    setElapsed(formatElapsed(activeTimer.startTime))
    return () => clearInterval(interval)
  }, [activeTimer?.startTime])

  const { data: tasksData, isLoading: tasksLoading } = useQuery(
    'my-tasks',
    () => globalTaskService.getMyTasks()
  )

  const { data: summaryData, isLoading: summaryLoading } = useQuery(
    'time-summary',
    () => globalTaskService.getTimeSummary()
  )

  const rawTasks = tasksData?.data?.tasks || []
  const todayTotal = summaryData?.data?.todayTotal || 0
  const weeklyDailyTotals = summaryData?.data?.weeklyDailyTotals || []
  const todayStr = new Date().toISOString().split('T')[0]

  // Apply custom order if in custom sort mode
  const orderedTasks = useMemo(() => {
    if (sortBy !== 'custom' || !customOrder) return rawTasks
    const orderMap = {}
    customOrder.forEach((id, i) => { orderMap[id] = i })
    const sorted = [...rawTasks].sort((a, b) => {
      const aIdx = orderMap[a.id] ?? 9999
      const bIdx = orderMap[b.id] ?? 9999
      return aIdx - bIdx
    })
    return sorted
  }, [rawTasks, customOrder, sortBy])

  // Filter tasks
  const filteredTasks = useMemo(() => {
    let result = [...orderedTasks]

    if (filterType === 'overdue') {
      result = result.filter(t =>
        t.due_date && isPast(new Date(t.due_date + 'T00:00:00')) && !isToday(new Date(t.due_date + 'T00:00:00')) && t.status?.category !== 'done'
      )
    } else if (filterType === 'due_today') {
      result = result.filter(t =>
        t.due_date && isToday(new Date(t.due_date + 'T00:00:00'))
      )
    }

    if (dateFilter) {
      result = result.filter(t => t.due_date === dateFilter)
    }

    // Sort (only when not custom)
    if (sortBy === 'due_date') {
      result.sort((a, b) => {
        if (!a.due_date && !b.due_date) return 0
        if (!a.due_date) return 1
        if (!b.due_date) return -1
        return a.due_date.localeCompare(b.due_date)
      })
    } else if (sortBy === 'priority') {
      result.sort((a, b) => (priorityOrder[b.priority] || 0) - (priorityOrder[a.priority] || 0))
    } else if (sortBy === 'project') {
      result.sort((a, b) => (a.project?.name || '').localeCompare(b.project?.name || ''))
    }

    return result
  }, [orderedTasks, filterType, dateFilter, sortBy])

  const chartData = weeklyDailyTotals.map((d) => ({
    date: d.date,
    day: format(parseISO(d.date), 'EEE'),
    hours: Math.round((d.totalSeconds / 3600) * 100) / 100,
    totalSeconds: d.totalSeconds,
  }))

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  )

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return
    const oldIndex = filteredTasks.findIndex(t => t.id === active.id)
    const newIndex = filteredTasks.findIndex(t => t.id === over.id)
    if (oldIndex === -1 || newIndex === -1) return

    const reordered = arrayMove(filteredTasks, oldIndex, newIndex)
    const newOrder = reordered.map(t => t.id)
    setCustomOrder(newOrder)
    setSortBy('custom')
    localStorage.setItem(TASK_ORDER_KEY, JSON.stringify(newOrder))
  }

  const handleStopConfirm = async (note) => {
    setStopLoading(true)
    try {
      if (conflictTask) {
        // Stop current timer, start new one on the conflict task
        await timeEntryService.stop(activeTimer.id, note)
        const res = await timeEntryService.start(conflictTask.id)
        startTimer({
          id: res.data.activeTimer.id,
          taskId: conflictTask.id,
          taskTitle: conflictTask.title,
          projectId: conflictTask.project?.id,
          projectName: conflictTask.project?.name || '',
          startTime: res.data.activeTimer.start_time
        })
        queryClient.invalidateQueries(['projects', String(conflictTask.project?.id), 'tasks'])
        toast.success('Previous timer stopped, new timer started')
        setConflictTask(null)
      } else {
        await timeEntryService.stop(activeTimer.id, note)
        stopTimer()
        toast.success('Timer stopped')
      }
      queryClient.invalidateQueries('time-summary')
      if (activeTimer?.projectId) {
        queryClient.invalidateQueries(['projects', String(activeTimer.projectId), 'tasks'])
      }
      setStopOpen(false)
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to stop timer')
    } finally {
      setStopLoading(false)
    }
  }

  const handleStartTimer = async (task) => {
    // If another task has a running timer, show conflict dialog
    if (activeTimer && activeTimer.taskId !== task.id) {
      setConflictTask(task)
      setStopOpen(true)
      return
    }
    setStartingTaskId(task.id)
    try {
      const res = await timeEntryService.start(task.id)
      startTimer({
        id: res.data.activeTimer.id,
        taskId: task.id,
        taskTitle: task.title,
        projectId: task.project?.id,
        projectName: task.project?.name || '',
        startTime: res.data.activeTimer.start_time
      })
      queryClient.invalidateQueries(['projects', String(task.project?.id), 'tasks'])
      queryClient.invalidateQueries('time-summary')
      toast.success('Timer started')
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to start timer')
    } finally {
      setStartingTaskId(null)
    }
  }

  const handleStopTimer = () => {
    setConflictTask(null)
    setStopOpen(true)
  }

  const handleTaskClick = (task) => {
    navigate(`/projects/${task.project?.id}/tasks/${task.id}`)
  }

  const handleBarClick = (data) => {
    if (dateFilter === data.date) {
      setDateFilter(null)
    } else {
      setDateFilter(data.date)
    }
  }

  const getOverdueInfo = (task) => {
    if (!task.due_date || task.status?.category === 'done') return null
    const dueDate = new Date(task.due_date + 'T00:00:00')
    if (isPast(dueDate) && !isToday(dueDate)) return 'overdue'
    if (isToday(dueDate)) return 'due_today'
    return null
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold">For you</h1>

      {/* Three cards in a row — same height */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="flex flex-col">
          <CardHeader className="pb-2 flex-none">
            <CardTitle className="text-sm font-medium text-muted-foreground">Currently Tracking</CardTitle>
          </CardHeader>
          <CardContent className="flex-1 flex items-center">
            {activeTimer ? (
              <div className="flex items-center justify-between gap-3 w-full">
                <div className="min-w-0">
                  <button
                    onClick={() => navigate(`/projects/${activeTimer.projectId}/tasks/${activeTimer.taskId}`)}
                    className="font-medium text-sm hover:text-primary transition-colors truncate block"
                  >
                    {activeTimer.taskTitle}
                  </button>
                  <p className="text-xs text-muted-foreground">{activeTimer.projectName}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <div className="flex items-center gap-1.5">
                    <Clock className="h-4 w-4 text-primary animate-pulse" />
                    <span className="font-mono text-sm font-medium tabular-nums">{elapsed}</span>
                  </div>
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { setConflictTask(null); setStopOpen(true) }}>
                    <Square className="h-3 w-3 mr-1 fill-current" />
                    Stop
                  </Button>
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No active timer</p>
            )}
          </CardContent>
        </Card>

        <Card className="flex flex-col">
          <CardHeader className="pb-2 flex-none">
            <CardTitle className="text-sm font-medium text-muted-foreground">Today's Total</CardTitle>
          </CardHeader>
          <CardContent className="flex-1 flex items-center">
            {summaryLoading ? (
              <Skeleton className="h-8 w-20" />
            ) : (
              <p className="text-2xl font-semibold">{formatDurationFromSeconds(todayTotal)}</p>
            )}
          </CardContent>
        </Card>

        <Card className="flex flex-col">
          <CardHeader className="pb-2 flex-none">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium text-muted-foreground">This Week</CardTitle>
              {dateFilter && (
                <Button
                  variant="ghost" size="sm" className="h-5 text-[10px] gap-1 px-1"
                  onClick={() => setDateFilter(null)}
                >
                  {format(parseISO(dateFilter), 'EEE, MMM d')}
                  <X className="h-3 w-3" />
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="flex-1 flex items-end pb-3">
            {summaryLoading ? (
              <Skeleton className="h-12 w-full" />
            ) : (
              <ResponsiveContainer width="100%" height={50}>
                <BarChart data={chartData} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
                  <XAxis dataKey="day" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} interval={0} />
                  <Tooltip
                    formatter={(value, name, props) => [formatDurationFromSeconds(props.payload.totalSeconds), 'Time']}
                    labelFormatter={(label) => label}
                    contentStyle={{ fontSize: 11, borderRadius: 8, padding: '4px 8px' }}
                  />
                  <Bar dataKey="hours" radius={[3, 3, 0, 0]} maxBarSize={24} cursor="pointer" onClick={handleBarClick}>
                    {chartData.map((entry, index) => {
                      const isSelected = entry.date === dateFilter
                      const isTodayBar = entry.date === todayStr
                      return (
                        <Cell
                          key={index}
                          fill={isSelected ? 'hsl(var(--primary))' : isTodayBar ? 'hsl(var(--primary) / 0.7)' : 'hsl(var(--primary) / 0.25)'}
                        />
                      )
                    })}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      {/* My Tasks — flat list with drag reorder */}
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h2 className="text-lg font-semibold">My Tasks</h2>
          <div className="flex items-center gap-2 flex-wrap">
            {['all', 'overdue', 'due_today'].map((f) => (
              <button
                key={f}
                onClick={() => setFilterType(f)}
                className={cn(
                  'px-2.5 py-1 rounded-full text-xs font-medium border transition-colors',
                  filterType === f
                    ? f === 'overdue' ? 'bg-red-50 text-red-700 border-red-200' :
                      f === 'due_today' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                      'bg-primary/10 text-primary border-primary/20'
                    : 'bg-muted text-muted-foreground border-transparent hover:bg-accent'
                )}
              >
                {f === 'all' ? 'All' : f === 'overdue' ? 'Overdue' : 'Due today'}
              </button>
            ))}

            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="h-7 w-[140px] text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="custom">Custom order</SelectItem>
                <SelectItem value="updated">Recently updated</SelectItem>
                <SelectItem value="due_date">Due date</SelectItem>
                <SelectItem value="priority">Priority</SelectItem>
                <SelectItem value="project">Project</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {tasksLoading ? (
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full rounded-lg" />
            ))}
          </div>
        ) : filteredTasks.length === 0 ? (
          <Card>
            <CardContent className="py-8 text-center">
              <CheckCircle2 className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">
                {filterType !== 'all' ? 'No matching tasks' : 'No tasks assigned to you'}
              </p>
              {filterType !== 'all' && (
                <Button variant="link" size="sm" className="text-xs mt-1" onClick={() => setFilterType('all')}>
                  Show all tasks
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={filteredTasks.map(t => t.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="border rounded-lg overflow-hidden">
                {filteredTasks.map((task) => {
                  const isTracking = activeTimer?.taskId === task.id
                  const overdueInfo = getOverdueInfo(task)
                  return (
                    <SortableTaskRow
                      key={task.id}
                      task={task}
                      isTracking={isTracking}
                      overdueInfo={overdueInfo}
                      onTaskClick={handleTaskClick}
                      onStartTimer={handleStartTimer}
                      onStopTimer={handleStopTimer}
                      startingTaskId={startingTaskId}
                      elapsed={elapsed}
                    />
                  )
                })}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </div>

      {activeTimer && (
        <TimerStopDialog
          open={stopOpen}
          onOpenChange={setStopOpen}
          taskTitle={activeTimer.taskTitle}
          elapsedLabel={formatDurationFromSeconds(Math.floor((Date.now() - new Date(activeTimer.startTime)) / 1000))}
          onConfirm={handleStopConfirm}
          loading={stopLoading}
        />
      )}
    </div>
  )
}

export default ForYou
